import { useId, useState } from 'react';

export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  display: string;
}

const SIZE = 240;
const R_OUTER = 110;
const R_INNER = 66;
const CX = SIZE / 2;
const CY = SIZE / 2;

/** Beyond this the slices stop being tellable apart; the tail folds into "Other". */
const MAX_SLICES = 5;

/** Fixed slot order — colour follows the entity, never its rank. */
const SERIES = [
  'var(--chart-series-1)',
  'var(--chart-series-2)',
  'var(--chart-series-3)',
  'var(--chart-series-4)',
  'var(--chart-series-5)',
];

const polar = (r: number, angle: number) => [
  CX + r * Math.cos(angle - Math.PI / 2),
  CY + r * Math.sin(angle - Math.PI / 2),
];

function arc(from: number, to: number) {
  const [x1, y1] = polar(R_OUTER, from);
  const [x2, y2] = polar(R_OUTER, to);
  const [x3, y3] = polar(R_INNER, to);
  const [x4, y4] = polar(R_INNER, from);
  const large = to - from > Math.PI ? 1 : 0;
  return [
    `M${x1} ${y1}`,
    `A${R_OUTER} ${R_OUTER} 0 ${large} 1 ${x2} ${y2}`,
    `L${x3} ${y3}`,
    `A${R_INNER} ${R_INNER} 0 ${large} 0 ${x4} ${y4}`,
    'Z',
  ].join(' ');
}

/**
 * Part-to-whole at a glance, capped at the slices the eye can actually compare.
 *
 * A donut is only honest for share — reading exact magnitudes off arc angles is
 * not something people do well, so the ranked bars beside it carry the precise
 * comparison and every slice is labelled here rather than relying on the arc.
 * Slices are separated by a 2px gap in the surface colour, not a border.
 */
export default function DonutChart({
  slices,
  total,
  totalLabel,
}: {
  slices: DonutSlice[];
  total: string;
  totalLabel: string;
}) {
  const [active, setActive] = useState<string | null>(null);
  const id = useId();

  const sum = slices.reduce((n, s) => n + s.value, 0);
  if (!sum) return <p className="m-0 text-base text-ink-muted">Nothing in this range.</p>;

  // Fold the tail into "Other" rather than inventing a sixth hue.
  const shown = slices.slice(0, MAX_SLICES);
  const rest = slices.slice(MAX_SLICES);
  const rows = rest.length
    ? [
        ...shown,
        {
          key: '__other',
          label: `Other (${rest.length})`,
          value: rest.reduce((n, s) => n + s.value, 0),
          display: '',
        },
      ]
    : shown;

  // Independently rounding each share makes the legend sum to 99% or 101%.
  // Largest-remainder: floor everything, then hand the leftover points to the
  // slices that lost the most in rounding, so the column always totals 100.
  const exact = rows.map((r) => (r.value / sum) * 100);
  const pcts = exact.map(Math.floor);
  let leftover = 100 - pcts.reduce((a, b) => a + b, 0);
  exact
    .map((e, i) => ({ i, rem: e - Math.floor(e) }))
    .sort((a, b) => b.rem - a.rem)
    .forEach(({ i }) => {
      if (leftover > 0) {
        pcts[i] += 1;
        leftover -= 1;
      }
    });

  let angle = 0;
  const wedges = rows.map((row, i) => {
    const sweep = (row.value / sum) * Math.PI * 2;
    const from = angle;
    angle += sweep;
    return {
      ...row,
      from,
      to: angle,
      pct: pcts[i],
      color: SERIES[i % SERIES.length],
    };
  });

  return (
    <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center sm:gap-6 print:flex-col print:items-stretch print:gap-3">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="w-[220px] shrink-0 print:mx-auto print:w-40"
        role="img"
        aria-label={`${totalLabel}: ${wedges.map((w) => `${w.label} ${w.pct}%`).join(', ')}`}
      >
        {wedges.map((w) => (
          <path
            key={w.key}
            d={arc(w.from, w.to)}
            fill={w.color}
            /* The gap between slices is surface colour, not a drawn border. */
            stroke="var(--surface)"
            strokeWidth="2"
            opacity={active && active !== w.key ? 0.35 : 1}
            onMouseEnter={() => setActive(w.key)}
            onMouseLeave={() => setActive(null)}
            style={{ transition: 'opacity 150ms' }}
          />
        ))}
        <text
          x={CX}
          y={CY - 4}
          textAnchor="middle"
          fontSize="26"
          fontWeight="600"
          fill="var(--ink)"
        >
          {total}
        </text>
        <text x={CX} y={CY + 18} textAnchor="middle" fontSize="13" fill="var(--ink-muted)">
          {totalLabel}
        </text>
      </svg>

      {/* A legend is always present for two or more series — identity is never
          carried by colour alone. */}
      <ul id={id} className="m-0 flex min-w-0 flex-1 list-none flex-col gap-2 p-0">
        {wedges.map((w) => (
          <li
            key={w.key}
            onMouseEnter={() => setActive(w.key)}
            onMouseLeave={() => setActive(null)}
            className={
              'flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors ' +
              (active === w.key ? 'bg-brand-wash' : '')
            }
          >
            <span
              aria-hidden="true"
              className="size-4 shrink-0 rounded-sm"
              style={{ background: w.color }}
            />
            <span className="min-w-0 flex-1 truncate text-base font-semibold">{w.label}</span>
            <span className="shrink-0 text-base text-ink-secondary tabular-nums">
              {w.display || ''}
            </span>
            <span className="w-12 shrink-0 text-right font-mono text-md text-ink-muted tabular-nums">
              {w.pct}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
